import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'

export const deviceModes = {
  Desktop: 'Desktop',
  Mobile: 'Mobile',
} as const
export type DeviceModeType = (typeof deviceModes)[keyof typeof deviceModes]

export const screenOrientations = {
  Portrait: 'Portrait',
  Landscape: 'Landscape',
} as const
export type ScreenOrientationType =
  (typeof screenOrientations)[keyof typeof screenOrientations]

type DeviceModeContextType = {
  deviceMode: DeviceModeType
  screenOrientation: ScreenOrientationType
  isDesktop: boolean
  isMobile: boolean
  isLandscape: boolean
  isPortrait: boolean
  setDeviceMode: (deviceMode: DeviceModeType) => void
  setScreenOrientation: (screenOrientation: ScreenOrientationType) => void
}

export const DeviceModeContext = createContext<DeviceModeContextType>({
  deviceMode: deviceModes.Desktop,
  screenOrientation: screenOrientations.Portrait,
  isDesktop: true,
  isMobile: false,
  isLandscape: false,
  isPortrait: true,
  setDeviceMode: (deviceMode: DeviceModeType) =>
    // biome-ignore lint/suspicious/noConsole: exception
    console.warn(`no device mode provided ${deviceMode}`),
  setScreenOrientation: (screenOrientation: ScreenOrientationType) =>
    // biome-ignore lint/suspicious/noConsole: exception
    console.warn(`no screen orientation provided ${screenOrientation}`),
})

export const useDeviceModeContext = () => useContext(DeviceModeContext)

export const DeviceModeProvider = (props: PropsWithChildren) => {
  const [deviceMode, setDeviceMode] = useState<DeviceModeType>(
    deviceModes.Desktop,
  )

  const [screenOrientation, setScreenOrientation] =
    useState<ScreenOrientationType>(screenOrientations.Portrait)

  // reset screen orientation back to default on desktop device mode switch
  useLayoutEffect(() => {
    if (deviceMode === deviceModes.Desktop) {
      setScreenOrientation(screenOrientations.Portrait)
    }
  }, [deviceMode])

  return (
    <DeviceModeContext.Provider
      value={{
        deviceMode,
        screenOrientation,
        isDesktop: deviceMode === deviceModes.Desktop,
        isMobile: deviceMode === deviceModes.Mobile,
        isLandscape: screenOrientation === screenOrientations.Landscape,
        isPortrait: screenOrientation === screenOrientations.Portrait,
        setDeviceMode,
        setScreenOrientation,
      }}
      {...props}
    />
  )
}
